<template>
  <v-menu
    offset-y
    bottom
    right
    :z-index="zIndex"
    min-width="260"
    max-width="260"
    max-height="380"
    v-model="open"
    :loading="loading"
    :close-on-content-click="false"
  >
    <template v-slot:activator="{ on }">
      <span v-on="disabled ? null : on" class="width-fit-content">
        <slot></slot>
      </span>
    </template>

    <v-sheet color="white" class="pa-0">
      <div class="item-selector item-search-popover">
        <div class="item-search-popover-main-button">
          <template v-if="!hideSearch">
            <v-text-field
              dense
              clearable
              hide-details
              class="item-search-bar pa-2 mt-0"
              data-testid="searchPopover-input-search"
              prepend-inner-icon="mdi-magnify"
              v-model="searchTerm"
              autofocus
              :placeholder="$t('common.search')"
              :disabled="loading"
            />
            <v-divider />
          </template>
          <div v-if="loading" class="search-popover-skeletons">
            <v-skeleton-loader class="my-3 mx-4" type="text" />
            <v-skeleton-loader
              type="list-item-avatar-two-line,
                  list-item-avatar-two-line,
                  list-item-avatar-two-line,
                  list-item-avatar-two-line,
                  list-item-avatar-two-line,
                  list-item-avatar-two-line,
                  list-item-avatar-two-line,
                  list-item-avatar-two-line"
            />
          </div>
        </div>
        <template v-if="searchTerm">
          <v-list
            v-if="matchingItems.length && !loading"
            dense
            color="transparent"
            class="pa-1"
          >
            <div v-for="item in matchingItems" :key="item.id">
              <ListItem
                class="pl-1 pr-3 py-0"
                :item="item"
                :searchTerm="searchTerm"
                :selected="item.isSelected"
                @click="item.isSelected ? onRemove(item) : onSelect(item)"
              />
            </div>
          </v-list>
        </template>
        <template v-else>
          <v-list
            v-if="selectedItems.length && !loading"
            dense
            data-tourid="item-selector-items"
            color="transparent"
            class="pa-1"
          >
            <div
              class="caption blue-grey--text text--darken-1 px-3 py-2 d-flex"
            >
              {{ $t('common.selected') }}
              <span class="pr-2"> &nbsp;({{ selectedItems.length }}) </span>
              <span class="ml-auto pl-2 cursor-pointer" @click="onClearAll">
                {{ $t('common.clear_all') }}</span
              >
            </div>
            <ListItem
              v-for="item in selectedItems"
              class="ps-1 pe-3 py-0"
              selected
              :hideIcon="hideIcon"
              :key="item.id"
              :item="item"
              :searchTerm="searchTerm"
              @click="onRemove(item)"
            />
          </v-list>
          <v-list
            v-if="unselectedItems.length && !loading"
            dense
            color="transparent"
            class="pa-1"
          >
            <div class="caption blue-grey--text text--darken-1 px-3 py-2">
              {{ label }}
              <span v-if="searchTerm">({{ unselectedItems.length }})</span>
            </div>
            <div v-for="item in unselectedItems" :key="item.id">
              <ListItem
                class="pl-1 pr-3 py-0"
                :hideIcon="hideIcon"
                :item="item"
                :searchTerm="searchTerm"
                @click="onSelect"
              />
            </div>
          </v-list>
        </template>

        <div
          v-if="!matchingItems.length && !loading"
          class="caption pa-4 blue-grey--text"
        >
          {{ $t('common.no_items_found') }}
        </div>
      </div>
    </v-sheet>
  </v-menu>
</template>

<script>
import _ from 'lodash'

import mixin from '@/mixins/mixin.vue'

import ListItem from './ListItem.vue'

export default {
  name: 'SearchPopover',

  mixins: [mixin],

  components: {
    ListItem,
  },

  props: {
    selectedItems: Array,
    label: String,
    options: {
      type: Array,
      default: () => [],
    },
    disabled: Boolean,
    hideSearch: {
      type: Boolean,
      default: false,
    },
    hideIcon: {
      type: Boolean,
      default: false,
    },
    selectionLimit: {
      type: Number,
      default: null,
    },
    value: {
      type: Array,
      default: () => [],
    },
    loading: Boolean,
    type: String,
    zIndex: { type: Number, default: 203 },
  },

  data() {
    return {
      open: false,
      searchTerm: '',
      initialLoadFinished: false,
    }
  },

  watch: {
    searchTerm: _.debounce(function(term) {
      this.searchItems(term)
    }, 1000),

    open(isOpening) {
      if (!this.initialLoadFinished && isOpening) {
        this.initialLoadFinished = true
        this.searchItems()
      }
    },
  },

  computed: {
    unselectedItems() {
      return _.differenceBy(
        _.uniqBy([...this.options], 'id'),
        this.selectedItems,
        'id'
      ).map(item => {
        return { ...item, isSelected: false }
      })
    },

    matchingItems() {
      const selectedItemsIdsSet = new Set(this.value)
      return this.options.map(item => {
        return {
          ...item,
          isSelected: selectedItemsIdsSet.has(item.id),
        }
      })
    },
  },

  methods: {
    async searchItems(terms = '') {
      this.$emit('search', terms)
    },

    onSelect(element) {
      let itemIds = [element.id]

      if (this.selectionLimit) {
        const ids = _.take(
          _.uniq([...this.value, ...itemIds]),
          this.selectionLimit
        )
        if (this.selectionLimit === ids.length)
          this.showError(
            this.$t('common.selection_limit_error', {
              selectionLimit: this.selectionLimit,
              type: this.type || this.$t('common.item_other'),
            })
          )

        return this.$emit('change', {
          ids,
          items: _.take(this.selectedItems, this.selectionLimit),
        })
      }
      this.$emit('change', {
        ids: _.uniq([...this.value, ...itemIds]),
        items: this.selectedItems,
      })
    },

    onRemove(element) {
      return this.$emit('change', {
        ids: this.value.filter(itemId => itemId !== element.id),
        items: this.selectedItems.filter(
          selectedItem => selectedItem.id !== element.id
        ),
      })
    },

    onClearAll() {
      return this.$emit('change', { ids: [], items: [] })
    },
  },
}
</script>
<style lang="scss">
.item-search-popover {
  .item-search-bar {
    font-size: 12px !important;
    .v-input__slot::after {
      border-style: none !important;
    }
    .v-input__slot::before {
      border-style: none !important;
    }
    .v-input__slot .v-text-field__slot input {
      padding-top: 0;
    }
    .v-input__append-inner button.v-icon {
      color: $color-blue-grey !important;
    }
    .v-input__prepend-inner i.v-icon {
      color: $color-blue-grey;
    }
  }
}
.search-popover-skeletons
  .v-skeleton-loader
  .v-skeleton-loader__list-item-avatar-two-line {
  height: 40px;
  margin: 4px 0;
  .v-skeleton-loader__avatar {
    margin-left: 4px;
    margin-right: 8px;
    width: 24px;
    height: 24px;
  }
}
.item-search-popover-advanced-button {
  position: sticky;
  bottom: 0;
  border-top: 1px solid $color-blue-grey-lighten2;
  background-color: $color-white;
}

.item-search-popover-main-button {
  position: sticky;
  top: 0;
  background-color: $color-white;
  z-index: 5;
}
</style>
